'use memo';

import type { ContractDashboardRow, VendorBankDetailsStatuses } from '@urbanx/agx.campaigns.client';
import {
  AgxAvatar,
  AgxBadge,
  AgxSpacer,
  AgxTable,
  AgxTag,
  AgxTypography,
  IColumnConfig,
  Icon_FileTextOutline,
  SortOrder,
} from '@urbanx/agx.ui-components2';
import { cleanTwoLineAddress, PropertyAddress } from '@urbanx/agx-ui-components';
import { getAgencyInitials } from 'utils/agencyUtils';
import { AgencyLookupValue } from './useContractsDashboard';
import slugify from 'slugify';
import {
  formatSettlementDateBadge,
  formatVendorBankDetails,
  statusBadgeColor,
} from './ContractDashboardBadges';
import { formatTaskStageLabel, TaskOverflowTag } from './ContractDashboardTaskStages';
import './ContractsDashboard.scss';

interface CampaignsDashboardTableProps {
  rows: ContractDashboardRow[];
  agencyLookup: Record<string, AgencyLookupValue>;
  isPending: boolean;
  isAgenciesPending: boolean;
  onContractRowClick: (index: number) => void;
  onSortSettlement: (sortOrder: SortOrder) => void;
}

const CampaignsDashboardTable = ({
  rows,
  agencyLookup,
  isPending,
  isAgenciesPending,
  onContractRowClick,
  onSortSettlement,
}: CampaignsDashboardTableProps) => {
  const dashboardColumns: (string | IColumnConfig)[] = [
    { name: 'Contract', maxColWidth: 250 },
    'Address',
    'Agency',
    'Status',
    'Tasks',
    {
      name: 'Settlement',
      sortable: true,
      sortFunction: onSortSettlement,
      defaultSortOrder: 1,
    },
    { name: 'Vendor Bank Details', align: 'right' },
  ];

  const tableRows = rows.map((row, index) => {
    const agencyName = row.agencyId ? agencyLookup[row.agencyId]?.name : undefined;
    const agencyInitials = getAgencyInitials(agencyName);
    const cleanAddress = row.address
      ? cleanTwoLineAddress(row.address as PropertyAddress)
      : ['Unknown address'];
    const [addressLineOne, addressLineTwo] = Array.isArray(cleanAddress)
      ? cleanAddress
      : [cleanAddress];

    const taskStages = row.taskStages ?? [];
    const visibleTaskStages = taskStages.slice(0, 2);
    const remainingTaskStages = taskStages.slice(visibleTaskStages.length);
    const remainingTaskCount = remainingTaskStages.length;
    const remainingTaskStagesTooltip = remainingTaskStages
      .map((taskStage) => formatTaskStageLabel(taskStage))
      .join('\n');

    return {
      Contract: (
        <AgxSpacer direction="horizontal" contentAlignment="center" gap={'2xs'}>
          <div className="contractDashboard_iconContainer">
            <Icon_FileTextOutline height={14} width={14} />
          </div>
          <div style={{ minWidth: 0 }}>
            <AgxTypography
              id={`campaign-dashboard-status-${index}`}
              size="small"
              type="body_ui"
              style={{
                color: 'var(--text-secondary)',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {row.contractName}
            </AgxTypography>
          </div>
        </AgxSpacer>
      ),
      Address: (
        <AgxSpacer contentAlignment="start" gap={'3xs'}>
          <AgxTypography
            id={`campaign-dashboard-status-${index}`}
            size="small"
            type="body_ui"
            color="var(--text-secondary)"
          >
            {addressLineOne ?? 'Unknown address'}
          </AgxTypography>
          <AgxTypography
            id={`campaign-dashboard-status-${index}`}
            size="small"
            type="body_ui"
            color="var(--text-secondary)"
          >
            {addressLineTwo ?? ''}
          </AgxTypography>
        </AgxSpacer>
      ),
      Agency: (
        <AgxSpacer direction="horizontal" gap="spacing-md" contentAlignment="center">
          {row.agencyId && agencyLookup[row.agencyId]?.logoUrl && (
            <AgxAvatar
              type="image"
              size="sm"
              imageUri={agencyLookup[row.agencyId]?.logoUrl ?? ''}
            />
          )}
          {row.agencyId && !agencyLookup[row.agencyId]?.logoUrl && (
            <AgxAvatar type="text-dark" size="sm" text={agencyInitials ?? 'UA'} />
          )}

          <AgxTypography
            id={`campaign-dashboard-agency-${index}`}
            size="small"
            type="body_ui"
            color="var(--text-secondary)"
          >
            {agencyName ?? '--'}
          </AgxTypography>
        </AgxSpacer>
      ),
      Status: (
        <AgxBadge
          type="colourfill"
          variant="normal"
          size="md"
          text={row.status}
          colour={statusBadgeColor(row.status)}
        />
      ),
      Tasks: row.taskStages?.length ? (
        <AgxSpacer direction="horizontal" className="tasksTagContainer">
          {visibleTaskStages.map((taskStage) => (
            <AgxTag
              key={slugify(taskStage)}
              id={`campaign-dashboard-tasks-${index}-${slugify(taskStage)}`}
              color="var(--text-black)"
            >
              {formatTaskStageLabel(taskStage)}
            </AgxTag>
          ))}
          {remainingTaskCount > 0 && (
            <TaskOverflowTag
              id={`campaign-dashboard-tasks-${index}-remaining`}
              remainingTaskCount={remainingTaskCount}
              tooltip={remainingTaskStagesTooltip}
            />
          )}
        </AgxSpacer>
      ) : (
        '--'
      ),
      Settlement: formatSettlementDateBadge(row.settlementDate, row.status),
      'Vendor Bank Details': formatVendorBankDetails(
        row.vendorBankDetailsStatus as unknown as VendorBankDetailsStatuses
      ),
    };
  });

  return (
    <AgxSpacer direction="horizontal" fill>
      <AgxSpacer direction="vertical" gap={'sm'} fill>
        <AgxTable
          columns={dashboardColumns}
          rows={tableRows}
          variant={!isPending && tableRows.length ? 'default' : 'minimal'}
          showLoadingState={isPending && tableRows.length === 0}
          tableStyle="square"
          stickyHeader
          noDataLabel={isAgenciesPending || isPending ? 'Loading...' : 'No contract found.'}
          noDataGlyphType="coin"
          onRowClick={!isPending && tableRows.length ? onContractRowClick : undefined}
        />
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default CampaignsDashboardTable;
